import { useEffect, useMemo, useState } from 'react'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import ForumRoundedIcon from '@mui/icons-material/ForumRounded'
import { Button } from '../Common'
import { AppDialog } from '../Common/AppDialog'
import { subscribePublicProfile, type PublicProfile } from '../../services/publicProfileService'
import { ProfileDetailsPage } from '../Profile/ProfileDetailsPage'
import { useChatEntryForMember } from './useChatEntryForMember'

type MemberProfileModalProps = {
  userId: string
  onClose: () => void
}

/**
 * Portal popup that shows another workspace member's "Your Page > Your
 * Details" surface in read-only mode. Reuses ProfileDetailsPage with the
 * `targetUserId` prop, which hides all edit affordances and streams the
 * target user's users/{uid} document instead of the current session's.
 *
 * Adds a "Send message" toolbar that opens the workspace chat with either
 * an @mention prefilled in the composer or a 2-member private DM channel
 * (found by roles-map match; created if none exists) via useChatEntryForMember.
 */
export const MemberProfileModal = ({ userId, onClose }: MemberProfileModalProps) => {
  const [profile, setProfile] = useState<PublicProfile | null>(null)
  const [busy, setBusy] = useState<'ping' | 'dm' | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const { pingInWorkspaceChat, openPrivateChat, canPing, canOpenPrivate } = useChatEntryForMember()

  useEffect(() => {
    if (typeof document === 'undefined') return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  useEffect(() => {
    if (!userId) return undefined
    const stop = subscribePublicProfile(userId, (nextProfile) => {
      setProfile(nextProfile)
    })
    return () => stop()
  }, [userId])

  const displayName = useMemo(() => {
    if (!profile) return ''
    if (profile.displayName && profile.displayName.trim()) return profile.displayName.trim()
    const composed = [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim()
    if (composed) return composed
    if (profile.username) return profile.username
    return 'Member'
  }, [profile])

  const handlePing = () => {
    if (!canPing || !displayName) return
    setBusy('ping')
    try {
      pingInWorkspaceChat(displayName)
      onClose()
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not open workspace chat.')
    } finally {
      setBusy(null)
    }
  }

  const handleDm = async () => {
    if (!canOpenPrivate) return
    setBusy('dm')
    setMessage(null)
    try {
      await openPrivateChat(userId, displayName || 'Member')
      onClose()
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not open a private chat.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <AppDialog
      open
      size="lg"
      title="Your Details"
      onDismiss={onClose}
      actions={[]}
    >
      <p className="dropdownLabel">Member profile</p>
      <div className="memberProfileModalToolbar" role="toolbar" aria-label="Message member">
        <div className="memberProfileModalToolbarCopy">
          <strong>Reach out</strong>
          <span>Message {displayName || 'this member'} in workspace chat, or open a private thread just between the two of you.</span>
        </div>
        <div className="memberProfileModalToolbarActions">
          <Button
            variant="secondary"
            size="sm"
            loading={busy === 'ping'}
            disabled={!canPing || !displayName || busy !== null}
            onClick={handlePing}
            icon={<ChatBubbleOutlineRoundedIcon fontSize="small" />}
          >
            Ping in workspace chat
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={busy === 'dm'}
            disabled={!canOpenPrivate || busy !== null}
            onClick={() => void handleDm()}
            icon={<ForumRoundedIcon fontSize="small" />}
          >
            Open private chat
          </Button>
        </div>
        {message ? <div className="memberProfileModalToolbarMessage">{message}</div> : null}
      </div>
      <div className="memberProfileModalBody">
        <ProfileDetailsPage targetUserId={userId} />
      </div>
    </AppDialog>
  )
}

export default MemberProfileModal
